import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Page from "../../../../components/Layout/Page";
import { useIsMobile } from "../../../../helpers/ui";
import { DesktopHeader, MobileHeader } from "../Account/components";
import {
  employeeAccountPath,
  employeeDebitCardChangeAddressPath,
  employeeDebitCardInfoPath,
  employeeDebitCardOrderNewPath,
} from "../../../../routes/paths";
import HeaderBreadcrumbs from "../../../../components/HeaderBreadcrumbs";
import T from "../../../../components/Typography";
import ResponsePageContent from "../../../../components/Layout/ResponsivePageContent";

import {
  SectionFlexColumn,
  SectionFlexRow,
} from "../../../../components/General";
import Button from "../../../../components/Button";
import Loader from "../../../../components/Loader";
import ErrorPage from "../../../../components/ErrorPage";
import useGetMailingAddress from "../../../../hooks/queries/employee/useGetMailingAddress";
import { colors } from "../../../../styles";
import Checkbox from "../../../../components/Checkbox";
import useOrderNewCard from "../../../../hooks/mutations/employee/useOrderNewCard";
import { QueryUserMailingAddressResponse } from "../../../../types";

export const OrderNewCardContents = ({
  addressData,
  handleSave,
  navigateBackPath,
  agreeText,
}: {
  addressData: QueryUserMailingAddressResponse;
  handleSave: () => void;
  navigateBackPath: string;
  agreeText?: string;
}) => {
  const [agreed, setAgreed] = useState(false);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  return (
    <SectionFlexColumn sx={{ alignItems: "center", width: "100%" }}>
      <T variant="mdReg">Your new card will be shipped to:</T>
      <SectionFlexColumn sx={{ textAlign: "center", marginTop: "8px" }}>
        <T variant="lgMed">{addressData.shipping_address.street}</T>
        <T variant="lgMed">{addressData.shipping_address.unit}</T>
        <T variant="lgMed">{`${addressData.shipping_address.city}, ${addressData.shipping_address.state} ${addressData.shipping_address.zip_code}`}</T>
      </SectionFlexColumn>
      <T
        variant="mdReg"
        color={colors.info_blue}
        sx={{ marginTop: "8px", cursor: "pointer" }}
        onClick={() =>
          navigate(employeeDebitCardChangeAddressPath, {
            state: { navigateBackPath },
          })
        }
      >
        Change Address
      </T>

      <div style={{ width: isMobile ? "100%" : "50%" }}>
        <SectionFlexRow
          sx={{ margin: "32px 0" }}
          onClick={() => setAgreed(!agreed)}
        >
          <Checkbox checked={agreed} />
          <T variant="smReg" sx={{ marginLeft: "8px" }}>
            {agreeText ||
              "I reviewed my mailing address and I understand my current card can no longer be used"}
          </T>
        </SectionFlexRow>
        <Button noMargin onClick={handleSave} disabled={!agreed}>
          Order New Card
        </Button>
      </div>
    </SectionFlexColumn>
  );
};

const OrderNewCard = () => {
  const { data: addressData, status } = useGetMailingAddress();
  const { mutateAsync: orderNewCard, status: orderNewCardStatus } =
    useOrderNewCard();
  const routeState = useLocation().state;
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const title = "Order a New Card";
  const subtitle =
    "Once you order a new card, we will turn off your existing card and it can't be used again.";
  const breadcrumbs = ["My Account", "Debit Card", title];

  if (status === "loading") return <Loader />;
  if (orderNewCardStatus === "loading")
    return <Loader text="Ordering card..." />;
  if (status === "error") return <ErrorPage />;

  const doBack = () => {
    // Optional route prop to pass in to define which page to navigate back to
    let path = employeeDebitCardInfoPath;
    if (routeState?.navigateBackPath) {
      path = routeState.navigateBackPath;
    }

    navigate(path);
  };

  const handleSave = async () => {
    const response = await orderNewCard();
    if (response.status && response.status === "success") {
      toast.success("Your new card has been ordered.");
      doBack();
    }
  };

  return (
    <Page>
      {isMobile ? (
        <MobileHeader
          title={title}
          showBack
          showClose
          handleBack={doBack}
          closeRoutePath={employeeAccountPath}
        />
      ) : (
        <DesktopHeader>
          <HeaderBreadcrumbs breadcrumbs={breadcrumbs} handleBack={doBack} />
        </DesktopHeader>
      )}
      <ResponsePageContent title={title} subtitle={subtitle}>
        <OrderNewCardContents
          addressData={addressData}
          handleSave={handleSave}
          navigateBackPath={employeeDebitCardOrderNewPath}
        />
      </ResponsePageContent>
    </Page>
  );
};

export default OrderNewCard;
